import { useAuthStore } from "../stores/authStore";
import { useFyStore } from "../stores/fyStore";

type CacheScope = "tenant" | "tenant-fy" | "global";
type StorageMode = "memory" | "local";

type CacheEntry<T> = {
  data: T;
  expiresAt: number;
  tags: string[];
};

type CachedQueryOptions<T> = {
  key: string;
  tags: string[];
  fetcher: () => Promise<T>;
  ttlMs?: number;
  scope?: CacheScope;
  storage?: StorageMode;
};

const DEFAULT_TTL_MS = 5 * 60 * 1000;
const STORAGE_PREFIX = "transporterp:master-cache:";

const memoryCache = new Map<string, CacheEntry<unknown>>();
const inflightRequests = new Map<string, Promise<unknown>>();
const tagIndex = new Map<string, Set<string>>();

function isBrowser() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function getScopePrefix(scope: CacheScope) {
  if (scope === "global") {
    return "global";
  }

  const tenantId = useAuthStore.getState().tenantId ?? "tenant-1";
  if (scope === "tenant") {
    return `tenant:${tenantId}`;
  }

  const fyCode = useFyStore.getState().activeFyCode ?? "fy-default";
  return `tenant:${tenantId}:fy:${fyCode}`;
}

function buildScopedKey(scope: CacheScope, key: string) {
  return `${getScopePrefix(scope)}:${key}`;
}

function readLocalEntry<T>(scopedKey: string) {
  if (!isBrowser()) return null;

  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${scopedKey}`);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as CacheEntry<T>;
    if (!parsed || typeof parsed.expiresAt !== "number") return null;

    if (Date.now() >= parsed.expiresAt) {
      window.localStorage.removeItem(`${STORAGE_PREFIX}${scopedKey}`);
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function writeLocalEntry<T>(scopedKey: string, entry: CacheEntry<T>) {
  if (!isBrowser()) return;

  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${scopedKey}`, JSON.stringify(entry));
  } catch {
    // Ignore storage quota or serialization failures; memory cache still works.
  }
}

function deleteLocalEntry(scopedKey: string) {
  if (!isBrowser()) return;
  window.localStorage.removeItem(`${STORAGE_PREFIX}${scopedKey}`);
}

function registerTags(scopedKey: string, tags: string[]) {
  tags.forEach((tag) => {
    const existing = tagIndex.get(tag) ?? new Set<string>();
    existing.add(scopedKey);
    tagIndex.set(tag, existing);
  });
}

function unregisterKey(scopedKey: string) {
  for (const [tag, keys] of tagIndex.entries()) {
    if (!keys.delete(scopedKey)) continue;
    if (keys.size === 0) {
      tagIndex.delete(tag);
    }
  }
}

function storeEntry<T>(scopedKey: string, entry: CacheEntry<T>, storage: StorageMode) {
  memoryCache.set(scopedKey, entry as CacheEntry<unknown>);
  registerTags(scopedKey, entry.tags);

  if (storage === "local") {
    writeLocalEntry(scopedKey, entry);
  }
}

function readEntry<T>(scopedKey: string, storage: StorageMode) {
  const memoryEntry = memoryCache.get(scopedKey) as CacheEntry<T> | undefined;
  if (memoryEntry && Date.now() < memoryEntry.expiresAt) {
    return memoryEntry;
  }

  if (memoryEntry) {
    unregisterKey(scopedKey);
    memoryCache.delete(scopedKey);
  }

  if (storage !== "local") {
    return null;
  }

  const localEntry = readLocalEntry<T>(scopedKey);
  if (!localEntry) {
    return null;
  }

  memoryCache.set(scopedKey, localEntry as CacheEntry<unknown>);
  registerTags(scopedKey, localEntry.tags);
  return localEntry;
}

/**
 * Reusable master-data cache.
 * Future reference:
 * 1. Use tagged keys so mutations can invalidate related lookup/list entries together.
 * 2. Scope cache entries by tenant or tenant+financial-year to avoid cross-context leaks.
 * 3. Dedupe inflight requests so repeated UI opens do not fan out duplicate API calls.
 */
export async function getCachedMasterData<T>({
  key,
  tags,
  fetcher,
  ttlMs = DEFAULT_TTL_MS,
  scope = "tenant",
  storage = "local"
}: CachedQueryOptions<T>) {
  const scopedKey = buildScopedKey(scope, key);
  const cached = readEntry<T>(scopedKey, storage);
  if (cached) {
    return cached.data;
  }

  const existingRequest = inflightRequests.get(scopedKey) as Promise<T> | undefined;
  if (existingRequest) {
    return existingRequest;
  }

  const request = (async () => {
    const data = await fetcher();
    storeEntry(scopedKey, {
      data,
      expiresAt: Date.now() + ttlMs,
      tags
    }, storage);
    return data;
  })();

  inflightRequests.set(scopedKey, request as Promise<unknown>);

  try {
    return await request;
  } finally {
    inflightRequests.delete(scopedKey);
  }
}

export function invalidateMasterDataTags(tags: string[]) {
  const keysToClear = new Set<string>();

  tags.forEach((tag) => {
    const keys = tagIndex.get(tag);
    if (!keys) return;
    keys.forEach((key) => keysToClear.add(key));
  });

  keysToClear.forEach((key) => {
    memoryCache.delete(key);
    deleteLocalEntry(key);
    unregisterKey(key);
  });
}

export function buildMasterDataKey(name: string, params?: Record<string, unknown>) {
  if (!params || Object.keys(params).length === 0) {
    return name;
  }

  const normalized = Object.entries(params)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([paramKey, value]) => [paramKey, value ?? null]);

  return `${name}:${JSON.stringify(normalized)}`;
}
